import type { ReactNode } from 'react';
import { Plus, X } from '@phosphor-icons/react';
import { cn } from '@/lib/utils/cn';

export interface EditorSectionProps<T = unknown> {
  initial?: T;
  onDirty: () => void;
  onSubmit: (values: T) => Promise<void> | void;
  submitting?: boolean;
  onDraftChange?: (values: unknown) => void;
}

export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn('block', className)}>
      <span className="mb-2 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
        {label}
      </span>
      {children}
      {error ? (
        <span className="mt-1.5 block text-xs text-red-600">{error}</span>
      ) : hint ? (
        <span className="mt-1.5 block text-xs text-ink-muted">{hint}</span>
      ) : null}
    </label>
  );
}

export function BulletList({
  value,
  onChange,
  max = 8,
  placeholder = 'Add a bullet',
}: {
  value: ReadonlyArray<string>;
  onChange: (next: string[]) => void;
  max?: number;
  placeholder?: string;
}) {
  return (
    <div className="space-y-2">
      {value.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <span
            aria-hidden
            className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center text-ink-muted"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-ink" />
          </span>
          <input
            value={item}
            onChange={(e) => {
              const next = [...value];
              next[i] = e.target.value;
              onChange(next);
            }}
            placeholder={placeholder}
            className="w-full rounded-md border border-line bg-board px-3 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:border-ink focus:outline-none"
          />
          <button
            type="button"
            onClick={() => onChange(value.filter((_, k) => k !== i))}
            aria-label="Remove bullet"
            className="rounded-md border border-line bg-board p-2 text-ink-muted hover:border-red-300 hover:text-red-700"
          >
            <X size={12} weight="bold" />
          </button>
        </div>
      ))}
      {value.length < max ? (
        <button
          type="button"
          onClick={() => onChange([...value, ''])}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-ink hover:underline"
        >
          <Plus size={12} /> Add bullet
        </button>
      ) : null}
    </div>
  );
}

export const inputClasses =
  'w-full rounded-md border border-line bg-board px-3 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:border-ink focus:outline-none';

export const textareaClasses = `${inputClasses} resize-y`;

export function EditorForm({
  children,
  onSubmit,
  disabled,
  submitting,
}: {
  children: ReactNode;
  onSubmit: () => void;
  disabled?: boolean;
  submitting?: boolean;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-6"
    >
      {children}
      <div className="flex justify-end border-t border-line pt-6">
        <button
          type="submit"
          disabled={disabled || submitting}
          className="rounded-md bg-ink px-4 py-2 text-sm font-medium text-board transition-colors hover:bg-ink/90 disabled:opacity-60"
        >
          {submitting ? 'Saving…' : 'Save section'}
        </button>
      </div>
    </form>
  );
}
